import type { GranularityType } from "@/hooks/useEvolutionChartData";
import type { EstablishmentVolumePerDay } from "@/integrations/agreements/client";

export interface HeatmapRow {
  establishmentId: number;
  establishmentName: string;
  /** Uma entrada por período visível, na mesma ordem de `periods`. */
  cells: number[];
  totalVolume: number;
}

export interface HeatmapData {
  /** Chaves de período, ordenadas — `YYYY-MM-DD` ou `YYYY-MM`. */
  periods: string[];
  rows: HeatmapRow[];
  /** Maior volume de célula — base dos terços da escala de cor. */
  maxCellVolume: number;
}

/**
 * Escolhe a chave de período conforme a granularidade. Mesma regra do gráfico
 * "Coletas ao longo do tempo" (`collectionsEvolutionChartAggregation.ts`) — o
 * backend manda `date`/`week`/`month` na mesma linha justamente para os dois
 * widgets não terem definições divergentes de "semana".
 */
function getPeriodKey(
  row: EstablishmentVolumePerDay,
  granularity: GranularityType,
): string {
  if (granularity === "day") return row.date;
  if (granularity === "month") return row.month;
  return row.week;
}

/**
 * Reagrupa o grão diário `(unidade, dia)` nos baldes da granularidade
 * escolhida e monta o produto cartesiano `unidades × períodos`.
 *
 * O cartesiano é necessário porque o `GROUP BY` do backend só emite
 * `(unidade, dia)` que teve coleta — "sem coleta" não é "sem dado", e a grade
 * precisa das células zeradas para ficar alinhada.
 *
 * As linhas saem ordenadas por volume total no intervalo (decrescente), ordem
 * que não muda ao trocar a granularidade.
 */
export function buildHeatmapData(
  rows: EstablishmentVolumePerDay[],
  granularity: GranularityType,
): HeatmapData {
  if (!rows.length) return { periods: [], rows: [], maxCellVolume: 0 };

  const periodSet = new Set<string>();
  const byEstablishment = new Map<
    number,
    { name: string; total: number; volumeByPeriod: Map<string, number> }
  >();

  for (const row of rows) {
    const period = getPeriodKey(row, granularity);
    periodSet.add(period);

    let entry = byEstablishment.get(row.establishment_id);
    if (!entry) {
      entry = {
        name: row.establishment_name,
        total: row.establishment_total_volume,
        volumeByPeriod: new Map(),
      };
      byEstablishment.set(row.establishment_id, entry);
    }
    entry.volumeByPeriod.set(
      period,
      (entry.volumeByPeriod.get(period) ?? 0) + row.total_volume,
    );
  }

  // Chaves são ISO, então ordenam lexicograficamente na ordem cronológica.
  const periods = [...periodSet].sort((a, b) => a.localeCompare(b));

  let maxCellVolume = 0;
  const heatmapRows: HeatmapRow[] = [...byEstablishment.entries()]
    .sort(([, a], [, b]) => b.total - a.total)
    .map(([establishmentId, entry]) => {
      const cells = periods.map((period) => {
        const value = entry.volumeByPeriod.get(period) ?? 0;
        if (value > maxCellVolume) maxCellVolume = value;
        return value;
      });
      return {
        establishmentId,
        establishmentName: entry.name,
        cells,
        totalVolume: entry.total,
      };
    });

  return { periods, rows: heatmapRows, maxCellVolume };
}

const MONTH_ABBREVIATIONS = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

/**
 * Rótulo da coluna. Segue o Figma (node 233:2586), que usa mês abreviado no
 * modo mensal — formato diferente do eixo do gráfico de barras logo acima, o
 * que é intencional no design.
 *
 * `includeYear` desempata quando o intervalo cruza o ano: sem ele, jan/2025 e
 * jan/2026 virariam duas colunas "Jan" indistinguíveis (e o mesmo texto no
 * tooltip). Fica desligado por padrão para preservar o Figma no caso comum.
 */
export function formatHeatmapPeriodLabel(
  period: string,
  granularity: GranularityType,
  includeYear = false,
): string {
  const parts = period.split("-");
  const [year] = parts;
  const suffix = includeYear && year ? `/${year.slice(2)}` : "";

  if (granularity === "month") {
    const [, month] = parts;
    const index = Number(month) - 1;
    const abbreviation = MONTH_ABBREVIATIONS[index];
    return abbreviation ? `${abbreviation}${suffix}` : period;
  }
  const [, month, day] = parts;
  if (!month || !day) return period;
  return `${day}/${month}${suffix}`;
}

/** Se os períodos visíveis cruzam mais de um ano, o rótulo precisa do ano. */
export function periodsSpanMultipleYears(periods: string[]): boolean {
  if (periods.length < 2) return false;
  const years = new Set(periods.map((period) => period.slice(0, 4)));
  return years.size > 1;
}
